import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Sparkles, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import JobCard, { Listing } from '@/components/JobCard';
import AddonsPanel from '@/components/AddonsPanel';
import AccountNotices from '@/components/AccountNotices';
import JobAlertsPanel from '@/components/JobAlertsPanel';
import { LeadBalanceSection, MyLeadsSection } from '@/components/ContractorLeadTools';

import { fetchListings, fetchPurchasedIds } from '@/lib/marketplace';
import { localizedSlug } from '@/data/constants';

/**
 * SUBCONTRACTOR DASHBOARD.
 *
 * The lead balance, purchased-lead pipeline and membership summary come from
 * src/components/ContractorLeadTools.tsx, which the general-contractor
 * dashboard imports too — GCs buy leads as well, so there is exactly ONE
 * implementation of the buyer tooling for both roles.
 */
const SubDashboard: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const [jobs, setJobs] = useState<Listing[]>([]);
  const [purchasedIds, setPurchasedIds] = useState<string[]>([]);

  useEffect(() => {
    fetchListings({ limit: 12 }).then(setJobs).catch(() => setJobs([]));
    fetchPurchasedIds(profile?.id).then(setPurchasedIds).catch(() => setPurchasedIds([]));
  }, [profile?.id]);

  const myTrades = profile?.trades || [];
  const myAreas = (profile?.service_areas || []).map((s) => s.toLowerCase());
  const recommended = jobs.filter(
    (j) => myTrades.includes(j.trade) || myAreas.includes((j.city || '').toLowerCase())
  );

  return (
    <div>
      {/* SECTION 0 — MONTHLY LEAD BALANCE / COUNTDOWN */}
      <section className="mb-12">
        <LeadBalanceSection />
        {/* Payment, membership, credit and lead-unlock notices mirrored from
            the transactional emails, so delivery is never the only path. */}
        <AccountNotices />
      </section>

      {/* SECTION 1 — AVAILABLE JOBS */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
            <Search className="h-5 w-5 text-orange-500" /> {t('dash.availableJobs')}
          </h2>
          <Link to={path('/jobs')} className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-600">
            {t('common.viewAll')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {jobs.slice(0, 6).map((j) => (
            <JobCard
              key={j.id}
              job={j}
              unlocked={purchasedIds.includes(j.id)}
              onUnlock={() => window.location.assign(path(`/jobs/${localizedSlug(j.slug, j.trade, lang)}`))}
            />
          ))}
        </div>
      </section>

      {/* SECTION 2A — SAVED SEARCHES / JOB ALERTS
          Placed directly under the job feed: the contractor has just seen what
          is open, so "get told when the next one lands" is the natural step. */}
      <JobAlertsPanel />

      {/* SECTION 2 — RECOMMENDED */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <Sparkles className="h-5 w-5 text-orange-500" /> {t('dash.recommended')}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t('dash.recommendedSub')}</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {(recommended.length ? recommended : jobs.slice(6, 9)).slice(0, 3).map((j) => (
            <JobCard
              key={j.id}
              job={j}
              unlocked={purchasedIds.includes(j.id)}
              onUnlock={() => window.location.assign(path(`/jobs/${localizedSlug(j.slug, j.trade, lang)}`))}
            />
          ))}
        </div>
      </section>

      {/* SECTIONS 3 + 4 — MY PURCHASED LEADS and MY MEMBERSHIP (shared with GCs) */}
      <MyLeadsSection />

      <AddonsPanel />
    </div>
  );
};

export default SubDashboard;
